import { z } from "zod";
import { zPropertySchemaAssociationFormData } from "~/features/admin/propertySchemas";
import type {
  GetCategoriesQuery,
  GetCategoryDetailQuery,
  GetCategoryForTableViewQuery,
  ListCategorySchemaMigrationsQuery,
} from "./gqls";

/**
 * Form shape for create + edit. Includes propertySchemaAssociations so the
 * embedded schema editor stays type-aligned with `useForm`.
 */
export const zCategoryFormData = z.object({
  id: z.string().optional(),
  typeId: z.string().min(1),
  name: z.string().min(1),
  /** Full path of the parent category. `null` when this category is the root of a tree. */
  parentPath: z.string().nullable(),
  /** Last segment of this category's own path (e.g. "person" for "ctn.person"). Format is checked on submit
   *  together with the parent path so the user sees one translated message instead of zod's raw regex text. */
  pathSegment: z.string(),
  description: z.string(),
  jaLabel: z.string().nullable(),
  enLabel: z.string().nullable(),
  documentUrl: z.string().nullable(),
  relationTypeId: z.string().nullable(),
  propertySchemaAssociations: z.array(zPropertySchemaAssociationFormData).optional(),
});
export const zCategoryFormDataKeys = zCategoryFormData.keyof();
export type CategoryFormData = z.infer<typeof zCategoryFormData>;

/** Form data plus the full path derived from `parentPath` + `pathSegment` at submit time. */
export type CategoryUpsertFormData = CategoryFormData & { categoryPath: string };

/** One selectable parent in the category form, laid out in tree (depth-first) order. */
export interface ParentCategoryOption {
  id: string;
  name: string;
  path: string;
  /** 0 for a tree root ("ctn"). */
  depth: number;
}

export type CategoryRaw = GetCategoriesQuery["categories"][number];

export type CategoryDetailRaw = GetCategoryDetailQuery["categories"][number];

export type CategoryTableViewRaw = GetCategoryForTableViewQuery["categories"][number];

export type CategorySchemaMigration =
  ListCategorySchemaMigrationsQuery["cnutListCategorySchemaMigrations"]["migrations"][number];
export type CategorySchemaMigrationRunAction = "run" | "dryrun";

/** Hierarchical table row with optional resolved children. */
export type CategoryHierarchyNode = CategoryTableViewRaw & {
  children?: CategoryHierarchyNode[];
};

/** Intermediate structure used by the path-tree builder. Children are referenced by path. */
type CategoryHierarchyDraft = CategoryTableViewRaw & {
  children?: string[];
};

export type { CategoryHierarchyDraft };

/** UI-ready row passed from the container to the table. */
export interface CategoryRow {
  id: string;
  typeId: string;
  /** Canonical category name. Do not localize this column. */
  displayName: string;
  /** Strict per-locale label (no fallback). Empty when the row has no value for the current locale. */
  localeLabel: string;
  description: string;
  /** Relation type name for relation rows, empty string otherwise. */
  relationTypeLabel: string;
  documentUrl: string | null;
  isDeactivated: boolean;
}

interface RelationTypeLink {
  id: string;
  name: string;
}

interface RelationCategoryLink {
  id: string;
  label: string;
}

export interface RelationTypeRow {
  /** Relation type id + name. */
  relationType: RelationTypeLink;
  /** The other side of the relation (target for source-rels, source for target-rels). */
  otherCategory: RelationCategoryLink;
}

export interface PropertySchemaRow {
  /** Synthetic row id (association ids no longer exist in the effective query — see transformers.ts). */
  id: string;
  name: string;
  description: string;
  position: number;
  required: boolean;
  unique: boolean;
  propertyTypeId: string;
  propertyAttributeTypeId: string | null;
  selectOptions: Array<{ value: string; label?: { ja?: string; en?: string } }>;
  urlAllowList: Array<{ value: string; regex: string }>;
  /** True when the category's own property schema set defines this key (vs. inherited-only). */
  isOwn: boolean;
  /** True when an ancestor in the chain redeclares this key with a different type/attributeType.
   *  The definer (shallowest ancestor) still wins; this is a display-only flag (kept for Phase 3 UX). */
  conflict: boolean;
  /** The category that defines this key (the shallowest ancestor, or the category itself). */
  sourceCategoryId: string;
  sourceCategoryName: string;
}

/** Fields shown in the read-only detail page. */
export interface CategoryDetailUI {
  id: string;
  name: string;
  displayName: string;
  description: string;
  typeId: string;
  categoryPath: string;
  documentUrl: string | null;
  jaLabel: string | null;
  enLabel: string | null;
  isDeactivated: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  deactivatedAt: string | null;
  relationType: {
    id: string;
    name: string;
    source: RelationCategoryLink;
    target: RelationCategoryLink;
  } | null;
  sourceRelations: RelationTypeRow[];
  targetRelations: RelationTypeRow[];
  propertySchemas: PropertySchemaRow[];
}
